import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  Search, Bell, Command, Settings, LayoutDashboard, FileText, 
  Users, CreditCard, Box, Zap, ArrowRight, ShieldCheck, 
  Database, Activity, ClipboardCheck, HardDrive, HelpCircle, ChevronDown
} from "lucide-react";

const TopNav = ({ profile }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  console.log("ffffff",profile)

  // Expanded Enterprise Menu with Professional Filtering Categories
  const menuItems = [
    { label: "ANALYTICS DASHBOARD", path: "/dashboard", icon: <LayoutDashboard size={14}/>, category: "CORE_SYSTEM", tags: "stats, overview, charts" },
    { label: "UT INSPECTION REGISTRY", path: "/reports/ut", icon: <Activity size={14}/>, category: "DATA_REGISTRY", tags: "ultrasonic, thickness, reports" },
    { label: "PAUT PHASED ARRAY", path: "/reports/paut", icon: <Database size={14}/>, category: "DATA_REGISTRY", tags: "omni, scan, s-scan" },
    { label: "EQUIPMENT CALIBRATION", path: "/inventory/calibration", icon: <ClipboardCheck size={14}/>, category: "ASSET_MANAGEMENT", tags: "probes, wedges, blocks" },
    { label: "TECHNICIAN DATABASE", path: "/team/operators", icon: <Users size={14}/>, category: "RESOURCES", tags: "levels, certs, pcn" },
    { label: "CLOUD STORAGE STATUS", path: "/storage", icon: <HardDrive size={14}/>, category: "INFRASTRUCTURE", tags: "data, logs, archive" },
    { label: "BILLING & QUOTA", path: "/billing", icon: <CreditCard size={14}/>, category: "SYSTEM_FUEL", tags: "credits, invoices, fuel" },
    { label: "SYSTEM PARAMETERS", path: "/settings", icon: <Settings size={14}/>, category: "CONFIGURATION", tags: "api, security, setup" },
    { label: "TECHNICAL DOCUMENTATION", path: "/docs", icon: <HelpCircle size={14}/>, category: "SUPPORT", tags: "help, manual, asme" },
  ];

  const filteredResults = searchQuery 
    ? menuItems.filter(item => 
        item.label.toLowerCase().includes(searchQuery.toLowerCase()) || 
        item.tags.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) setShowResults(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  // Function to convert UID string to a stable 4-digit Number
const generateNumericID = (uid) => {
  if (!uid) return "0401";
  let hash = 0;
  for (let i = 0; i < uid.length; i++) {
    hash = uid.charCodeAt(i) + ((hash << 5) - hash);
  }
  // Ensure it's a positive 4-digit number
  return Math.abs(hash % 10000).toString().padStart(4, '0');
};

const operatorShortID = generateNumericID(profile?.uid);


  return (
    <header className="h-16 bg-white border-b border-slate-300 flex items-center justify-between px-8 sticky top-0 z-0 select-none">
      
      {/* 1. LEFT: SEARCH & QUICK FILTERS */}
      <div className="flex items-center gap-6 flex-1">
        <div className="relative" ref={searchRef}>
          <div className={`flex items-center gap-3 bg-slate-50 px-4 py-1.5 border transition-all w-[500px] ${showResults ? 'border-slate-900 bg-white ring-4 ring-slate-100' : 'border-slate-200'}`}>
            <Search className="text-slate-400" size={15} />
            <input 
              type="text" 
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setShowResults(true); }}
              onFocus={() => setShowResults(true)}
              placeholder="SEARCH BY USID, TAG, OR SYSTEM MODULE..." 
              className="bg-transparent border-none outline-none text-[10px] font-black w-full text-slate-900 placeholder:text-slate-400 uppercase tracking-widest"
            />
            <div className="flex items-center gap-1 px-1.5 py-0.5 border border-slate-200 bg-white text-[9px] font-black text-slate-400 shadow-sm">
              <Command size={9} /> K
            </div>
          </div>

          {/* ADVANCED SEARCH DROPDOWN */}
          {showResults && searchQuery.length > 0 && (
            <div className="absolute top-[110%] left-0 w-full bg-white border border-slate-900 shadow-[8px_8px_0px_0px_rgba(15,23,42,0.1)] z-[60]">
              <div className="p-2 border-b border-slate-100 bg-slate-50 flex gap-2">
                 <span className="text-[8px] font-black text-slate-400 px-2 py-1 border border-slate-200 bg-white uppercase">Filter: All Records</span>
              </div>
              <div className="max-h-[450px] overflow-y-auto">
                {filteredResults.length > 0 ? (
                  filteredResults.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      onClick={() => setShowResults(false)}
                      className="w-full flex items-center justify-between px-4 py-3 border-b border-slate-100 last:border-none hover:bg-slate-50 group transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-2 border border-slate-100 bg-white text-slate-400 group-hover:text-slate-900 group-hover:border-slate-300 transition-all">
                          {item.icon}
                        </div>
                        <div className="text-left">
                          <p className="text-[10px] font-black text-slate-900 tracking-wider leading-none">{item.label}</p>
                          <p className="text-[8px] font-bold text-slate-400 tracking-tight mt-1">{item.category}</p>
                        </div>
                      </div>
                      <ArrowRight size={12} className="text-slate-300 opacity-0 group-hover:opacity-100 transform translate-x-[-4px] group-hover:translate-x-0 transition-all" />
                    </Link>
                  ))
                ) : (
                  <div className="p-10 text-center">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em]">Query Returned No Results</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 2. RIGHT: STATUS, CREDITS & OPERATOR PROFILE */}
      <div className="flex items-center h-full">
        
        {/* SYSTEM STATUS INDICATOR */}
        <div className="hidden 2xl:flex items-center gap-6 px-8 h-full border-l border-slate-200 group cursor-help">
           <div className="flex flex-col items-end">
              <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Server Status</span>
              <span className="text-[9px] font-black text-emerald-600 flex items-center gap-1.5 mt-0.5">
                 <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                 OPERATIONAL
              </span>
           </div>
        </div>

        {/* SYSTEM FUEL (CREDITS) */}
        <div className="flex items-center gap-4 px-8 h-full border-l border-slate-200 bg-slate-50/30">
          <div className="flex flex-col items-end">
            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none">System Fuel</span>
            <span className="text-[12px] font-black text-slate-900 tabular-nums mt-1">
              {profile?.credits_remaining ?? "0.00"} <span className="text-[9px] text-slate-400 ml-0.5">CR</span>
            </span>
          </div>
          <div className="p-2 border border-slate-200 bg-white">
            <Zap size={14} className="text-slate-900 fill-slate-900" />
          </div>
        </div>

        {/* NOTIFICATIONS TRAY */}
        <button className="px-6 h-full border-l border-slate-200 text-slate-400 hover:text-slate-900 hover:bg-slate-50 transition-all relative group">
          <Bell size={18} />
          <div className="absolute top-5 right-5 w-2 h-2 bg-rose-600 border-2 border-white ring-1 ring-rose-200" />
        </button>

        {/* OPERATOR SESSION SECTION */}
        <div className="flex items-center gap-4 pl-8 h-full group cursor-pointer hover:bg-slate-50 transition-colors px-4 border-l border-slate-200">
          <div className="text-right">
            <p className="text-[11px] font-black text-slate-900 tracking-tight leading-none uppercase">
              {profile?.fullName || "A. MORE"}
            </p>
            <div className="flex items-center justify-end gap-2 mt-1.5">
              <span className="text-[8px] font-black text-slate-500 tracking-widest border border-slate-200 px-1.5 py-0.5 bg-white uppercase">
                 ID: OP-{operatorShortID}
              </span>
              <div className="flex items-center gap-1">
                 <span className="text-[8px] font-black text-emerald-600 uppercase tracking-tighter">
                   {profile?.ndtLevel || "LEVEL II"}
                 </span>
                 <ShieldCheck size={10} className="text-emerald-600" />
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="w-10 h-10 bg-slate-900 flex items-center justify-center font-black text-white text-xs border-2 border-slate-800 group-hover:bg-slate-800 transition-colors shadow-sm">
              {profile?.fullName?.charAt(0).toUpperCase() || "O"}
            </div>
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-white border border-slate-200 flex items-center justify-center">
               <ChevronDown size={8} className="text-slate-900" />
            </div>
          </div>
        </div>

      </div>
    </header>
  );
};

export default TopNav;